/**
 * Nachrichtenkatalog der Upload-Oberfläche – die Texte aus docs/UPLOAD-TEXTE.md mit ihren Schlüsseln.
 * Platzhalter in geschweiften Klammern werden zur Laufzeit ersetzt.
 */

export type Severity = 'hint' | 'warn' | 'error';

interface Message {
  severity: Severity;
  title: string;
  text?: string;
}

export const UPLOAD_TEXTS: Record<string, Message> = {
  'upload.hint.requirements': {
    severity: 'hint',
    title: 'So exportierst du dein Webinar-Video am besten',
    text: 'Format: MP4 (H.264) · Auflösung: 1920 × 1080 (Full HD), Seitenverhältnis 16:9 – bei reinen Bildschirmaufnahmen genügt 1280 × 720 · Bitrate: 4–8 Mbit/s bei 1080p · Ton: AAC – und prüfe vor dem Export, dass die Tonspur wirklich drauf ist · Länge: 45–75 Minuten haben sich bewährt. Höhere Auflösungen als 1080p bringen nichts: Wir spielen maximal Full HD aus.',
  },
  'upload.hint.resumable': {
    severity: 'hint',
    title: 'Der Upload läuft in Paketen. Wenn die Verbindung abbricht, kannst du an derselben Stelle weitermachen – du musst nicht von vorn anfangen.',
  },
  'upload.check.ok': { severity: 'hint', title: 'Datei geprüft – alles passt.', text: '{format} · {breite} × {hoehe} · {dauer} · {groesse}. Du kannst den Upload starten.' },
  'upload.warn.resolution_too_high': {
    severity: 'warn',
    title: 'Dein Video ist {breite} × {hoehe} – das ist mehr, als wir ausspielen.',
    text: 'Wir liefern maximal 1920 × 1080 aus. Die zusätzlichen Bildpunkte gehen bei der Verarbeitung verloren, der Upload dauert dadurch aber rund {faktor}-mal so lange – ohne dass das Ergebnis besser aussieht. Unsere Empfehlung: Exportiere das Video noch einmal in 1920 × 1080 und lade es dann hoch.',
  },
  'upload.warn.resolution_too_low': {
    severity: 'warn',
    title: 'Dein Video ist nur {breite} × {hoehe}.',
    text: 'Das ist niedriger als 1280 × 720 und wirkt im Webinar-Fenster schnell unscharf – besonders, wenn du Folien oder Text zeigst. Falls du eine höher aufgelöste Fassung hast, nimm lieber die.',
  },
  'upload.warn.aspect_ratio': {
    severity: 'warn',
    title: 'Dein Video hat ein ungewöhnliches Seitenverhältnis ({breite} × {hoehe}).',
    text: 'Der Webinar-Raum ist für 16:9 gebaut. Dein Video bekommt deshalb schwarze Balken an den Seiten oder oben und unten. Das funktioniert, sieht aber weniger professionell aus.',
  },
  'upload.error.container_unsupported': {
    severity: 'error',
    title: 'Dieses Dateiformat können wir nicht verarbeiten ({format}).',
    text: 'Bitte lade dein Video als MP4, MOV oder MKV hoch. MP4 mit H.264 ist die sicherste Wahl – nahezu jedes Schnittprogramm bietet das als Export-Voreinstellung an.',
  },
  'upload.error.file_too_large': {
    severity: 'error',
    title: 'Die Datei ist mit {groesse} zu groß. Die Obergrenze liegt bei 8 GB.',
    text: 'Das lässt sich fast immer mit den Export-Einstellungen lösen: 1080p bei 4–8 Mbit/s ergibt für ein einstündiges Webinar ungefähr 2–4 GB. Wenn dein Video deutlich darüber liegt, ist meist die Bitrate zu hoch eingestellt oder es wurde in 4K exportiert. Geht es wirklich nicht kleiner? Dann melde dich – wir können die Datei auch direkt auf den Server übertragen.',
  },
  'upload.error.duration_too_long': {
    severity: 'error',
    title: 'Dein Video ist {dauer} lang. Die Obergrenze liegt bei 180 Minuten.',
    text: 'Für ein Webinar ist das ohnehin sehr lang – erfahrungsgemäß steigen die meisten Teilnehmer nach 75 Minuten aus. Überlege, ob sich das Thema auf mehrere Webinare aufteilen lässt.',
  },
  'upload.warn.duration_very_short': {
    severity: 'warn',
    title: 'Dein Video ist nur {dauer} lang.',
    text: 'Das ist ungewöhnlich kurz für ein Webinar – hast du vielleicht die falsche Datei erwischt oder einen Export abgebrochen?',
  },
  'upload.error.width_exceeded': {
    severity: 'error',
    title: 'Dein Video ist {breite} Bildpunkte breit. Wir verarbeiten maximal 4096.',
    text: 'Bitte exportiere es in 1920 × 1080 – das ist die Auflösung, in der es später ohnehin ausgespielt wird.',
  },
  'upload.error.unreadable': {
    severity: 'error',
    title: 'Diese Datei lässt sich nicht lesen.',
    text: 'Möglicherweise ist sie beschädigt oder der Export wurde nicht sauber beendet. Öffne sie einmal in deinem Videoplayer: Wenn sie dort nicht läuft, exportiere sie bitte neu.',
  },
  'upload.progress': { severity: 'hint', title: 'Wird hochgeladen … {prozent} % · noch etwa {restzeit}', text: 'Du kannst dieses Fenster geöffnet lassen. Bei einer Unterbrechung machen wir automatisch weiter.' },
  'upload.paused_connection': {
    severity: 'hint',
    title: 'Verbindung unterbrochen.',
    text: 'Kein Problem – wir versuchen es weiter und setzen genau dort an, wo es aufgehört hat. Der bisherige Fortschritt bleibt erhalten.',
  },
  'upload.resumed': { severity: 'hint', title: 'Upload wird fortgesetzt bei {prozent} %.' },
  'upload.complete': {
    severity: 'hint',
    title: 'Upload abgeschlossen.',
    text: 'Wir bereiten dein Video jetzt auf – das dauert je nach Länge 30 bis 60 Minuten. Du kannst dieses Fenster schließen, wir schicken dir eine E-Mail, sobald es fertig ist.',
  },
  'upload.error.no_audio': {
    severity: 'error',
    title: 'Dein Video enthält keine Tonspur.',
    text: 'Das passiert beim Export aus Schnittprogrammen häufiger, als man denkt – meist ist im Export-Dialog der Ton nicht mit ausgewählt. Bitte exportiere das Video noch einmal mit Tonspur (AAC) und lade es erneut hoch.',
  },
  'upload.warn.silent_audio': {
    severity: 'warn',
    title: 'Die Tonspur ist da, aber fast durchgehend still.',
    text: 'Das deutet darauf hin, dass bei der Aufnahme das Mikrofon stummgeschaltet war oder die falsche Tonquelle aufgezeichnet wurde. Bitte hör in die Vorschau hinein, bevor du das Webinar scharfschaltest.',
  },
  'upload.error.codec_unsupported': {
    severity: 'error',
    title: 'Der Videocodec „{format}" lässt sich nicht verarbeiten.',
    text: 'Bitte exportiere dein Video als MP4 mit H.264 – das versteht jeder Browser und jedes Endgerät.',
  },
  'upload.error.mismatch': { severity: 'error', title: 'Die hochgeladene Datei stimmt nicht mit der Vorprüfung überein.', text: 'Vermutlich ist beim Übertragen etwas schiefgegangen. Bitte lade die Datei noch einmal hoch.' },
  'upload.processing': { severity: 'hint', title: 'Dein Video wird aufbereitet … {prozent} %', text: 'Wir erzeugen drei Qualitätsstufen, damit es auch bei schwacher Mobilverbindung flüssig läuft.' },
  'upload.processing.done': { severity: 'hint', title: 'Dein Video ist fertig aufbereitet.', text: '{breite} × {hoehe} · {dauer} · {stufen}. Bitte sieh dir die Vorschau an und prüfe Bild und Ton. Erst danach solltest du das Webinar scharfschalten.' },
  'upload.processing.failed': {
    severity: 'error',
    title: 'Bei der Aufbereitung ist etwas schiefgegangen.',
    text: 'Deine Datei ist noch da, es ging nichts verloren. Du kannst die Verarbeitung neu starten – schlägt sie erneut fehl, liegt es vermutlich an der Datei selbst. Dann hilft ein neuer Export als MP4 (H.264, 1080p).',
  },
};

export function uploadText(key: string, vars: Record<string, string | number> = {}): Message {
  const message = UPLOAD_TEXTS[key] ?? { severity: 'error' as const, title: key };
  const fill = (s: string) => s.replace(/\{(\w+)\}/g, (_, k: string) => (vars[k] !== undefined ? String(vars[k]) : `{${k}}`));
  return { severity: message.severity, title: fill(message.title), ...(message.text ? { text: fill(message.text) } : {}) };
}

export function formatBytes(bytes: number): string {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1).replace('.', ',')} GB`;
  if (bytes >= 1024 ** 2) return `${Math.round(bytes / 1024 ** 2)} MB`;
  return `${Math.round(bytes / 1024)} KB`;
}

export function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return m >= 60 ? `${Math.floor(m / 60)} Std. ${m % 60} Min.` : `${m}:${String(s).padStart(2, '0')} Min.`;
}
